const e = up.element;
const u = up.util;
up.ScriptGate = class ScriptGate {
    constructor(cspInfo = up.CSPInfo.none()) {
        let pageNonce = up.script.cspNonce();
        this._evalCallbackPolicy = new EvalCallbackPolicy(pageNonce, cspInfo);
        this._scriptElementPolicy = new BodyScriptElementPolicy(pageNonce, cspInfo);
        this._detachedAssetPolicy = new DetachedAssetPolicy(pageNonce, cspInfo);
    }
    warnOfUnsafeCSP() {
        if (up.script.config.cspWarnings) {
            this._evalCallbackPolicy.warnOfUnsafeCSP();
            this._scriptElementPolicy.warnOfUnsafeCSP();
        }
    }
    evalCallback(nonceableCallback, evalEnv) {
        const policy = this._evalCallbackPolicy;
        if (policy.passesItem(nonceableCallback.nonce)) {
            try {
                return nonceableCallback.unsafeEval(evalEnv);
            }
            catch (error) {
                if (error instanceof EvalError) {
                    throw new up.Blocked(error.message);
                }
                else {
                    throw error;
                }
            }
        }
        else {
            throw new up.Blocked('Blocked callback: ' + nonceableCallback.script);
        }
    }
    adoptNewFragment(fragment) {
        this._adoptScriptElements(fragment);
        this._adoptAttrCallbacks(fragment);
    }
    adoptDetachedHeadAsset(asset) {
        if (!up.script.isScript(asset))
            return;
        let item = new ScriptElementItem(asset);
        this._detachedAssetPolicy.processItem(item);
    }
    adoptRenderOptionsFromHeader(object) {
        let policy = this._evalCallbackPolicy;
        for (let key of Object.keys(object)) {
            let script = object[key];
            if (/^on[A-Z]/.test(key) && u.isString(script)) {
                let item = new ObjectPropertyItem(object, key);
                policy.processItem(item);
                object[key] = up.RenderOptions.parseCallback(key, object[key]);
            }
        }
    }
    _adoptScriptElements(root) {
        const policy = this._scriptElementPolicy;
        for (let script of up.script.findScripts(root)) {
            let item = new ScriptElementItem(script);
            policy.processItem(item);
        }
    }
    _adoptAttrCallbacks(root) {
        const policy = this._evalCallbackPolicy;
        for (let attribute of up.script.config.nonceableAttributes) {
            let matches = e.subtree(root, e.attrSelector(attribute));
            for (let element of matches) {
                let item = new AttributeItem(element, attribute, policy);
                policy.processItem(item);
            }
        }
    }
};
class Policy {
    constructor(pageNonce, cspInfo) {
        this.pageNonce = pageNonce;
        this.cspInfo = cspInfo;
        this._validNonces = new Set(cspInfo.nonces);
        if (pageNonce)
            this._validNonces.add(pageNonce);
    }
    isValidNonce(nonce) {
        return this._validNonces.has(nonce);
    }
    tryRewriteNonce(allowedItem, nonce = allowedItem.readNonce()) {
        if (this.pageNonce && this.isValidNonce(nonce)) {
            allowedItem.writeNonce(this.pageNonce);
        }
    }
    warnOfUnsafeCSP() {
        throw new up.NotImplemented();
    }
    processItem(item) {
        let nonce = item.readNonce();
        let blocked = !this.passesItem(nonce);
        if (blocked) {
            this.log(...item.logBlockedDescriptor());
            item.block();
        }
        else {
            this.tryRewriteNonce(item, nonce);
        }
    }
    passesItem(_itemNonce) {
        throw new up.NotImplemented();
    }
}
class DetachedAssetPolicy extends Policy {
    constructor(...args) {
        super(...args);
    }
    passesItem(_itemNonce) {
        return true;
    }
}
class ConfigurablePolicy extends Policy {
    constructor(configProp, pageNonce, cspInfo) {
        super(pageNonce, cspInfo);
        this._configProp = configProp;
        let policyString = up.script.config[configProp];
        if (policyString === 'auto')
            policyString = this.resolveAuto();
        if (policyString === 'nonce' && !this.pageNonce) {
            this.warn('Enforcing nonces requires a <meta name="csp-nonce">.');
            policyString = 'block';
        }
        this.policyString = policyString;
    }
    resolveAuto() {
        throw new up.NotImplemented();
    }
    passesItem(itemNonce) {
        switch (this.policyString) {
            case 'pass': return true;
            case 'block': return false;
            case 'nonce': return this.isValidNonce(itemNonce);
            default: up.fail('Unknown policy: %s = %o', this.configExpression(), this.policyString);
        }
    }
    log(...descriptorArgs) {
        up.puts(this.configExpression(), ...descriptorArgs);
    }
    warn(...descriptorArgs) {
        up.warn(this.configExpression(), ...descriptorArgs);
    }
    configExpression() {
        return 'up.script.config.' + this._configProp;
    }
}
class EvalCallbackPolicy extends ConfigurablePolicy {
    constructor(...args) {
        super('evalCallbackPolicy', ...args);
    }
    resolveAuto() {
        if (this.pageNonce) {
            return 'nonce';
        }
        else {
            return 'pass';
        }
    }
    warnOfUnsafeCSP() {
        if (this.policyString === 'pass' && this.cspInfo.isUnsafeEval()) {
            this.warn(`An 'unsafe-eval' CSP allows arbitrary [up-on...] callbacks. Consider setting ${this.configExpression()} = 'nonce'.`);
        }
    }
}
class BodyScriptElementPolicy extends ConfigurablePolicy {
    constructor(...args) {
        super('scriptElementPolicy', ...args);
    }
    resolveAuto() {
        if (this.cspInfo.isStrictDynamic()) {
            return 'nonce';
        }
        else {
            return 'pass';
        }
    }
    warnOfUnsafeCSP() {
        if (this.policyString === 'pass' && this.cspInfo.isStrictDynamic()) {
            this.warn(`A 'strict-dynamic' CSP allows arbitrary <script> elements in new fragments. Consider setting ${this.configExpression()} = 'nonce'.`);
        }
    }
}
class StringCallbackItem {
    readNonce() {
        return this._getCallback().nonce;
    }
    writeNonce(nonce) {
        let callback = this._getCallback();
        callback.nonce = nonce;
        this.writeCallbackString(callback.toString());
    }
    _getCallback() {
        return this._cacheCallback ||= up.NonceableCallback.fromString(this.readCallbackString());
    }
    logBlockedDescriptor() {
        throw new up.NotImplemented();
    }
    block() {
        this.writeCallbackString('/* blocked */');
    }
    readCallbackString() {
        throw new up.NotImplemented();
    }
    writeCallbackString(_newString) {
        throw new up.NotImplemented();
    }
}
class AttributeItem extends StringCallbackItem {
    constructor(element, attribute) {
        super();
        this._element = element;
        this._attribute = attribute;
    }
    logBlockedDescriptor() {
        return ['Blocked callback attribute: %s', e.attrSelector(this._attribute, this.readCallbackString())];
    }
    readCallbackString() {
        return this._element.getAttribute(this._attribute);
    }
    writeCallbackString(newString) {
        this._element.setAttribute(this._attribute, newString);
    }
}
class ObjectPropertyItem extends StringCallbackItem {
    constructor(object, key) {
        super();
        this._object = object;
        this._key = key;
    }
    logBlockedDescriptor() {
        return ['Blocked string callback: { %s: %o }', this._key, this.readCallbackString()];
    }
    readCallbackString() {
        return this._object[this._key];
    }
    writeCallbackString(newString) {
        this._object[this._key] = newString;
    }
}
class ScriptElementItem {
    constructor(element) {
        this._element = element;
    }
    readNonce() {
        return this._element.nonce;
    }
    writeNonce(nonce) {
        this._element.setAttribute('nonce', nonce);
    }
    logBlockedDescriptor() {
        return ['Blocked <script> element: %o', this._element];
    }
    block() {
        up.script.block(this._element);
    }
}
